/**
 * useContacts Hook
 *
 * CRUD for contacts, search/filter, favorite toggle via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  api,
  EmailContact,
  EmailContactListOptions,
  EmailCreateContactInput,
  EmailUpdateContactInput,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

interface UseContactsOptions {
  accountId: string | undefined;
  search?: string;
  isFavorite?: boolean;
  limit?: number;
  offset?: number;
  sort?: 'name' | 'lastContacted' | 'contactCount';
}

export function useContacts(options: UseContactsOptions) {
  const { accountId, ...filters } = options;
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.contacts({ accountId, ...filters }),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      const params: EmailContactListOptions = { accountId, ...filters };
      return api.listEmailContacts(params);
    },
    enabled: !!accountId,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.email.contacts({ accountId }) });
  }, [accountId, queryClient]);

  const createContact = useCallback(
    async (input: EmailCreateContactInput) => {
      const result = await api.createEmailContact(input);
      invalidate();
      return result;
    },
    [invalidate],
  );

  const updateContact = useCallback(
    async (contactId: string, input: EmailUpdateContactInput) => {
      if (!accountId) return;
      const result = await api.updateEmailContact(contactId, accountId, input);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const deleteContact = useCallback(
    async (contactId: string) => {
      if (!accountId) return;
      await api.deleteEmailContact(contactId, accountId);
      invalidate();
    },
    [accountId, invalidate],
  );

  const toggleFavorite = useCallback(
    async (contactId: string) => {
      if (!accountId) return;
      const result = await api.toggleEmailContactFavorite(contactId, accountId);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  return {
    contacts: queryResult.data?.contacts ?? ([] as EmailContact[]),
    total: queryResult.data?.total ?? 0,
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    createContact,
    updateContact,
    deleteContact,
    toggleFavorite,
  };
}
